import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Divider, Popover, Typography } from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  LaptopMac,
  NightsStay,
  RepeatRounded,
  WbSunny,
} from '@mui/icons-material';
import { MealPreference, RoutineActivityRecurring } from '@demind-inc/core';
import { debounce, isEmpty } from 'lodash';

import {
  useAuthContext,
  usePrepareScheduler,
  useUpdateMealPref,
  useUpdateWorkoutPref,
  useUpdateSchedulerPref,
} from '../../../data-access';
import {
  initialDefaultWorkHourPrefItemState,
  transformDBPrefToWorkHourPref,
  WorkHourPrefItemState,
} from '../../Scheduler/AIScheduler/reducer/workHourPrefItem.state';
import { CustomTimeSelector } from '../../common';
import { RoutineRecurringSelector, DAYS_OF_WEEK } from '../../Scheduler/common';

type MealKey = 'breakfast' | 'lunch' | 'dinner';
interface MealRow {
  id: MealKey;
  label: string;
}
const MEAL_ROWS: MealRow[] = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'dinner', label: 'Dinner' },
];

type OpenPickerKey =
  | 'breakfastStart'
  | 'breakfastEnd'
  | 'lunchStart'
  | 'lunchEnd'
  | 'dinnerStart'
  | 'dinnerEnd'
  | 'workoutStart'
  | 'workoutEnd';

type PrefSection = 'work' | 'meal' | 'workout' | null;

const makeTime = (hour: number, min: number) => dayjs().hour(hour).minute(min).second(0);

const createDefaultRecurring = (): RoutineActivityRecurring => ({
  sunday: { enabled: true },
  monday: { enabled: true },
  tuesday: { enabled: true },
  wednesday: { enabled: true },
  thursday: { enabled: true },
  friday: { enabled: true },
  saturday: { enabled: true },
});

const formatRecurringSummary = (recurring: RoutineActivityRecurring): string => {
  const enabled = DAYS_OF_WEEK.filter((d) => recurring[d.key]?.enabled);
  if (enabled.length === 7) return 'Every day';
  if (enabled.length === 0) return 'Never';
  const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const weekend = ['saturday', 'sunday'];
  const keys = enabled.map((d) => d.key as string);
  if (keys.length === 5 && weekdays.every((d) => keys.includes(d))) return 'Weekdays';
  if (keys.length === 2 && weekend.every((d) => keys.includes(d))) return 'Weekends';
  return enabled.map((d) => d.label).join(' ');
};

export const PowerUpPlanPreferences: React.FC = () => {
  const { user } = useAuthContext();
  const { schedulerPreferences, routine, updateRoutineActivity } = usePrepareScheduler();
  const { updateMealPref } = useUpdateMealPref();
  const { updateWorkoutPref } = useUpdateWorkoutPref();
  const { updateSchedulerPref } = useUpdateSchedulerPref();

  const [openPref, setOpenPref] = useState<PrefSection>(null);
  const togglePref = (section: Exclude<PrefSection, null>) =>
    setOpenPref((prev) => (prev === section ? null : section));

  /* ── Work hours ── */
  const [workHours, setWorkHours] = useState<WorkHourPrefItemState>(
    initialDefaultWorkHourPrefItemState
  );
  const [bufferMinutes] = useState(10);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    if (!schedulerPreferences || isEmpty(schedulerPreferences)) return;
    setWorkHours(transformDBPrefToWorkHourPref(schedulerPreferences));
  }, [schedulerPreferences]);

  const debouncedSaveWorkTime = useCallback(
    debounce((wh: WorkHourPrefItemState, buf: number) => {
      if (!user?.preferenceId) return;
      const endDayOffset = wh.start.time >= wh.end.time ? 1 : 0;
      updateSchedulerPref({
        preferenceId: user.preferenceId,
        preferences: {
          workTime: { start: wh.start.time, end: wh.end.time, endDayOffset },
          bufferMinutes: buf,
        },
      });
    }, 800),
    [user.preferenceId]
  );

  const handleWorkTimeChange = (type: 'start' | 'end', value: Dayjs | null) => {
    if (!value) return;
    setWorkHours((prev) => {
      const next = { ...prev, [type]: { ...prev[type], time: value.format('HH:mm') } };
      debouncedSaveWorkTime(next, bufferMinutes);
      return next;
    });
  };

  const isOvernight = workHours.start.time >= workHours.end.time;

  /* ── Meal preferences ── */
  const [mealTimes, setMealTimes] = useState<Record<MealKey, { start: Dayjs; end: Dayjs }>>({
    breakfast: { start: makeTime(7, 0), end: makeTime(7, 30) },
    lunch: { start: makeTime(12, 0), end: makeTime(12, 30) },
    dinner: { start: makeTime(18, 0), end: makeTime(18, 30) },
  });
  const [mealRecurring, setMealRecurring] = useState<Record<MealKey, RoutineActivityRecurring>>({
    breakfast: createDefaultRecurring(),
    lunch: createDefaultRecurring(),
    dinner: createDefaultRecurring(),
  });
  const [mealNotes, setMealNotes] = useState('');
  const [mealRecurringAnchor, setMealRecurringAnchor] = useState<
    Partial<Record<MealKey, HTMLElement | null>>
  >({});

  const workoutActivityId = useMemo(
    () =>
      routine?.routineActivities?.find((a) => !a.hidden && a.category?.id === 'default_exercise')
        ?.routineActivityId ?? null,
    [routine?.routineActivities]
  );

  const debouncedSaveMeal = useCallback(
    debounce(
      (
        times: Record<MealKey, { start: Dayjs; end: Dayjs }>,
        recurring: Record<MealKey, RoutineActivityRecurring>,
        notes: string
      ) => {
        const mealPref: MealPreference = {};
        (Object.keys(times) as MealKey[]).forEach((key) => {
          mealPref[key] = {
            earliestTime: times[key].start.format('HH:mm'),
            latestTime: times[key].end.format('HH:mm'),
            durationMin: Math.max(15, times[key].end.diff(times[key].start, 'minute')),
            recurring: recurring[key],
          };
        });
        if (notes.trim()) mealPref.note = notes.trim();
        updateMealPref({
          preferenceId: user.preferenceId!,
          preferences: mealPref,
          routineId: user.routineId!,
        });
      },
      800
    ),
    [user.preferenceId, user.routineId]
  );

  /* ── Workout preferences ── */
  const [workoutStart, setWorkoutStart] = useState<Dayjs>(makeTime(17, 30));
  const [workoutEnd, setWorkoutEnd] = useState<Dayjs>(makeTime(18, 30));
  const [workoutRecurring, setWorkoutRecurring] =
    useState<RoutineActivityRecurring>(createDefaultRecurring());
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [workoutRecurringAnchor, setWorkoutRecurringAnchor] = useState<HTMLElement | null>(null);

  const debouncedSaveWorkoutRoutine = useCallback(
    debounce((start: Dayjs, end: Dayjs, recurring: RoutineActivityRecurring) => {
      if (!user.routineId || !user.preferenceId || !workoutActivityId) return;
      void updateRoutineActivity(workoutActivityId, {
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm'),
        durationMin: end.diff(start, 'minute'),
        recurring,
      });
    }, 800),
    [updateRoutineActivity, user.preferenceId, user.routineId, workoutActivityId]
  );

  const debouncedSaveWorkoutNote = useCallback(
    debounce((notes: string) => {
      if (!user.preferenceId) return;
      void updateWorkoutPref({
        preferenceId: user.preferenceId,
        preferences: { note: notes.trim() || undefined },
      });
    }, 800),
    [user.preferenceId]
  );

  /* ── Open pickers ── */
  const [openPicker, setOpenPicker] = useState<Record<OpenPickerKey, boolean>>({
    breakfastStart: false,
    breakfastEnd: false,
    lunchStart: false,
    lunchEnd: false,
    dinnerStart: false,
    dinnerEnd: false,
    workoutStart: false,
    workoutEnd: false,
  });
  const setPickerOpen = (key: OpenPickerKey, open: boolean) =>
    setOpenPicker((prev) => ({ ...prev, [key]: open }));

  return (
    <div className="power-up-plan-page__pref-cards">
      {/* Work hours */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => togglePref('work')}
        >
          <LaptopMac style={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Work hours
          </Typography>
          <Typography variant="caption" className="power-up-plan-page__pref-summary">
            {workHours.start.time} – {workHours.end.time}
          </Typography>
          {openPref === 'work' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openPref === 'work' && (
          <>
            <Divider className="connect-world-page__divider" />
            <div className="work-pref-page__work-hours" style={{ padding: '16px' }}>
              <div className="work-pref-page__hour-row">
                <WbSunny className="work-pref-page__hour-icon" />
                <CustomTimeSelector
                  value={dayjs(workHours.start.time, 'HH:mm')}
                  onChange={(v) => handleWorkTimeChange('start', v)}
                  amPm
                  open={startOpen}
                  setOpen={setStartOpen}
                  className="work-pref-page__time-selector"
                  autocompleteClassName="work-pref-page__time-selector__input"
                  paperClassName="work-pref-page__time-selector__paper"
                />
              </div>
              <Typography variant="body2" className="work-pref-page__dash">
                –
              </Typography>
              <div className="work-pref-page__hour-row">
                <NightsStay className="work-pref-page__hour-icon" />
                <CustomTimeSelector
                  value={dayjs(workHours.end.time, 'HH:mm')}
                  onChange={(v) => handleWorkTimeChange('end', v)}
                  amPm
                  open={endOpen}
                  setOpen={setEndOpen}
                  className="work-pref-page__time-selector"
                  autocompleteClassName="work-pref-page__time-selector__input"
                  paperClassName="work-pref-page__time-selector__paper"
                />
                {isOvernight && (
                  <Typography variant="caption" className="work-pref-page__next-day-label">
                    Next day (+1D)
                  </Typography>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Meal times */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => togglePref('meal')}
        >
          <span className="power-up-plan-page__pref-emoji">🍽️</span>
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Meal times
          </Typography>
          {openPref === 'meal' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openPref === 'meal' && (
          <>
            <Divider className="connect-world-page__divider" />
            <div className="quick-pref-page__section-body">
              <div className="quick-pref-page__rows">
                {MEAL_ROWS.map((row) => {
                  const startKey = `${row.id}Start` as OpenPickerKey;
                  const endKey = `${row.id}End` as OpenPickerKey;
                  const anchor = mealRecurringAnchor[row.id] ?? null;
                  return (
                    <div key={row.id} className="quick-pref-page__row-group">
                      <div className="quick-pref-page__row">
                        <Typography variant="body2" className="quick-pref-page__row-label">
                          {row.label}
                        </Typography>
                        <div className="quick-pref-page__time-pickers">
                          <CustomTimeSelector
                            value={mealTimes[row.id].start}
                            onChange={(v) => {
                              if (!v) return;
                              setMealTimes((p) => {
                                const next = { ...p, [row.id]: { ...p[row.id], start: v } };
                                debouncedSaveMeal(next, mealRecurring, mealNotes);
                                return next;
                              });
                            }}
                            amPm
                            open={openPicker[startKey]}
                            setOpen={(o) => setPickerOpen(startKey, o)}
                            className="quick-pref-page__time-selector"
                            autocompleteClassName="quick-pref-page__time-selector__input"
                            paperClassName="quick-pref-page__time-selector__paper"
                          />
                          <Typography variant="body2" className="quick-pref-page__dash">
                            –
                          </Typography>
                          <CustomTimeSelector
                            value={mealTimes[row.id].end}
                            onChange={(v) => {
                              if (!v) return;
                              setMealTimes((p) => {
                                const next = { ...p, [row.id]: { ...p[row.id], end: v } };
                                debouncedSaveMeal(next, mealRecurring, mealNotes);
                                return next;
                              });
                            }}
                            amPm
                            open={openPicker[endKey]}
                            setOpen={(o) => setPickerOpen(endKey, o)}
                            className="quick-pref-page__time-selector"
                            autocompleteClassName="quick-pref-page__time-selector__input"
                            paperClassName="quick-pref-page__time-selector__paper"
                          />
                          <button
                            type="button"
                            className="quick-pref-page__repeat-btn"
                            onClick={(e) =>
                              setMealRecurringAnchor((p) => ({
                                ...p,
                                [row.id]: e.currentTarget,
                              }))
                            }
                          >
                            <RepeatRounded fontSize="inherit" />
                            <span>{formatRecurringSummary(mealRecurring[row.id])}</span>
                          </button>
                        </div>
                      </div>
                      <Popover
                        open={Boolean(anchor)}
                        anchorEl={anchor}
                        onClose={() => setMealRecurringAnchor((p) => ({ ...p, [row.id]: null }))}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{
                          paper: { className: 'quick-pref-page__recurring-popover' },
                        }}
                      >
                        <RoutineRecurringSelector
                          recurring={mealRecurring[row.id]}
                          onDaySelect={(day) =>
                            setMealRecurring((p) => {
                              const next = {
                                ...p,
                                [row.id]: {
                                  ...p[row.id],
                                  [day]: { enabled: !p[row.id][day]?.enabled },
                                },
                              };
                              debouncedSaveMeal(mealTimes, next, mealNotes);
                              return next;
                            })
                          }
                          hasSmallContainer
                        />
                      </Popover>
                    </div>
                  );
                })}
              </div>
              <div className="quick-pref-page__notes-block">
                <Typography variant="body2" className="quick-pref-page__notes-label">
                  Notes / anything else?
                </Typography>
                <textarea
                  className="quick-pref-page__notes-input"
                  value={mealNotes}
                  onChange={(e) => {
                    setMealNotes(e.target.value);
                    debouncedSaveMeal(mealTimes, mealRecurring, e.target.value);
                  }}
                  placeholder="Add notes for meal planning. (e.g. I'm vegan.)"
                  rows={2}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Workout */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => togglePref('workout')}
        >
          <span className="power-up-plan-page__pref-emoji">🏋️</span>
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Workout
          </Typography>
          <Typography variant="caption" className="power-up-plan-page__pref-summary">
            {workoutStart.format('h:mm A')} – {workoutEnd.format('h:mm A')}
          </Typography>
          {openPref === 'workout' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openPref === 'workout' && (
          <>
            <Divider className="connect-world-page__divider" />
            <div className="quick-pref-page__section-body">
              <div className="quick-pref-page__rows">
                <div className="quick-pref-page__row-group">
                  <div className="quick-pref-page__row">
                    <Typography variant="body2" className="quick-pref-page__row-label">
                      Timing
                    </Typography>
                    <div className="quick-pref-page__time-pickers">
                      <CustomTimeSelector
                        value={workoutStart}
                        onChange={(v) => {
                          if (!v) return;
                          setWorkoutStart(v);
                          debouncedSaveWorkoutRoutine(v, workoutEnd, workoutRecurring);
                        }}
                        amPm
                        open={openPicker.workoutStart}
                        setOpen={(o) => setPickerOpen('workoutStart', o)}
                        className="quick-pref-page__time-selector"
                        autocompleteClassName="quick-pref-page__time-selector__input"
                        paperClassName="quick-pref-page__time-selector__paper"
                      />
                      <Typography variant="body2" className="quick-pref-page__dash">
                        –
                      </Typography>
                      <CustomTimeSelector
                        value={workoutEnd}
                        onChange={(v) => {
                          if (!v) return;
                          setWorkoutEnd(v);
                          debouncedSaveWorkoutRoutine(workoutStart, v, workoutRecurring);
                        }}
                        amPm
                        open={openPicker.workoutEnd}
                        setOpen={(o) => setPickerOpen('workoutEnd', o)}
                        className="quick-pref-page__time-selector"
                        autocompleteClassName="quick-pref-page__time-selector__input"
                        paperClassName="quick-pref-page__time-selector__paper"
                      />
                      <button
                        type="button"
                        className="quick-pref-page__repeat-btn"
                        onClick={(e) => setWorkoutRecurringAnchor(e.currentTarget)}
                      >
                        <RepeatRounded fontSize="inherit" />
                        <span>{formatRecurringSummary(workoutRecurring)}</span>
                      </button>
                    </div>
                  </div>
                  <Popover
                    open={Boolean(workoutRecurringAnchor)}
                    anchorEl={workoutRecurringAnchor}
                    onClose={() => setWorkoutRecurringAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { className: 'quick-pref-page__recurring-popover' } }}
                  >
                    <RoutineRecurringSelector
                      recurring={workoutRecurring}
                      onDaySelect={(day) =>
                        setWorkoutRecurring((p) => {
                          const next = { ...p, [day]: { enabled: !p[day]?.enabled } };
                          debouncedSaveWorkoutRoutine(workoutStart, workoutEnd, next);
                          return next;
                        })
                      }
                      hasSmallContainer
                    />
                  </Popover>
                </div>
              </div>
              <div className="quick-pref-page__notes-block">
                <Typography variant="body2" className="quick-pref-page__notes-label">
                  Notes / anything else?
                </Typography>
                <textarea
                  className="quick-pref-page__notes-input"
                  value={workoutNotes}
                  onChange={(e) => {
                    setWorkoutNotes(e.target.value);
                    debouncedSaveWorkoutNote(e.target.value);
                  }}
                  placeholder="Add notes for workout planning. (e.g. I run 3km usually.)"
                  rows={2}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
